import type { DataSourceDef } from '@safetyhub/shared';

// ============================================================
// 조립 데이터 소스 dry-run 미리보기 — 순수 추출/표시 로직 (Phase B-3)
// ============================================================
//
// 서버 실데이터 미리보기(previewReportComposerByContext) 응답의 content에서 특정 선언
// (def.key)의 조립 산출을 뽑아, 카드 안에 표로 보여주기 좋은 형태로 정규화한다.
//
// 서버 적재 계약(report-composer.service assembleTemplateDataSources):
//  - cardinality 'list'   → content[def.key] = 행 객체 배열(키=def.fields[].field)
//  - cardinality 'single' → content[def.key] = 객체 1개(키=def.fields[].field) 또는 null(주 소스 미해석)
//
// 이 모듈은 순수(React/DOM 무관)라 유닛 테스트로 검증한다.

/** 표시 상한 — 상위 N행만 표로, 초과분은 "…외 M행"으로 요약. */
export const DRY_RUN_ROW_LIMIT = 5;

/** 모달 표시 상한 — 모달은 폭이 넓어 인라인보다 많은 행을 보인다. */
export const DRY_RUN_MODAL_ROW_LIMIT = 20;

/** null/누락 값을 나타내는 표시 문자열. */
export const DRY_RUN_EMPTY_CELL = '—';

/** dry-run 결과 칼럼(list) — def.fields에서 파생. */
export interface DryRunColumn {
  field: string;
  label: string;
}

/** list 형상 결과 — 상위 rows(절단됨) + 총/숨김 행수. */
export interface DryRunListResult {
  kind: 'list';
  columns: DryRunColumn[];
  /** 표시 행(상한까지 절단) */
  rows: Record<string, unknown>[];
  /** 조립된 전체 행수 */
  totalRows: number;
  /** 상한 초과로 숨긴 행수(totalRows - rows.length) */
  hiddenRows: number;
}

/** single 형상 결과 — 필드-값 2열. */
export interface DryRunSingleResult {
  kind: 'single';
  fields: { field: string; label: string; value: unknown }[];
}

/** 산출 부재·조립 실패 — 원인 안내 문구 동반. */
export interface DryRunEmptyResult {
  kind: 'empty';
  reason: string;
}

export type DryRunResult = DryRunListResult | DryRunSingleResult | DryRunEmptyResult;

/** def.fields → 표시 칼럼. label 없으면 field key를 라벨로. */
function toColumns(def: DataSourceDef): DryRunColumn[] {
  return def.fields.map((f) => ({ field: f.field, label: f.label?.trim() || f.field }));
}

/** 객체 후보인지(배열/null 제외). */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/**
 * 서버 미리보기 content에서 def의 조립 산출을 추출해 표시용 결과로 정규화한다.
 *
 * @param content previewReportComposerByContext 응답의 content(키=def.key 등)
 * @param def     대상 조립 데이터 소스 선언
 * @param limit   list 표시 상한(기본 DRY_RUN_ROW_LIMIT)
 */
export function extractDataSourceDryRun(
  content: Record<string, unknown> | null | undefined,
  def: DataSourceDef,
  limit: number = DRY_RUN_ROW_LIMIT,
): DryRunResult {
  const columns = toColumns(def);
  const hasKey = !!content && Object.prototype.hasOwnProperty.call(content, def.key);
  const value = hasKey ? content![def.key] : undefined;

  if (!hasKey) {
    return {
      kind: 'empty',
      reason:
        '조립 산출이 응답에 없습니다. 선언(주 소스·필드)이 올바른지, 저장 후 다시 시도하세요.',
    };
  }

  if (def.cardinality === 'list') {
    if (!Array.isArray(value)) {
      // 주 소스 미해석이면 서버가 빈 배열을 넣는 게 정상 — 배열이 아니면 형상 불일치/조립 실패.
      return {
        kind: 'empty',
        reason:
          '조립 결과가 행 배열이 아닙니다. 주 소스 형상(list)과 선언이 일치하는지 확인하세요.',
      };
    }
    if (value.length === 0) {
      return {
        kind: 'empty',
        reason:
          '조립된 행이 없습니다. 선택한 실 데이터 컨텍스트에 주 소스 데이터가 있는지 확인하세요.',
      };
    }
    const safeLimit = limit > 0 ? limit : DRY_RUN_ROW_LIMIT;
    const rows = value.slice(0, safeLimit).map((r) => (isPlainObject(r) ? r : {}));
    // 필드 선언이 없으면 표시 행 키의 합집합으로 칼럼 파생(빈 칼럼 표 방지).
    let cols = columns;
    if (cols.length === 0) {
      const keys = new Set<string>();
      for (const r of rows) for (const k of Object.keys(r)) keys.add(k);
      cols = Array.from(keys).map((k) => ({ field: k, label: k }));
    }
    return {
      kind: 'list',
      columns: cols,
      rows,
      totalRows: value.length,
      hiddenRows: Math.max(0, value.length - rows.length),
    };
  }

  // single
  if (value === null || value === undefined) {
    return {
      kind: 'empty',
      reason:
        '조립 결과(묶음)가 없습니다. 주 소스가 선택한 컨텍스트에서 해석되는지 확인하세요.',
    };
  }
  if (!isPlainObject(value)) {
    return {
      kind: 'empty',
      reason: '조립 결과가 객체가 아닙니다. 주 소스 형상(single)과 선언을 확인하세요.',
    };
  }
  const fieldDefs =
    columns.length > 0
      ? columns
      : Object.keys(value).map((k) => ({ field: k, label: k }));
  return {
    kind: 'single',
    fields: fieldDefs.map((c) => ({
      field: c.field,
      label: c.label,
      value: value[c.field],
    })),
  };
}

/** 셀 값 표시 문자열 — null/undefined/빈 문자열은 EMPTY_CELL, 객체/배열은 JSON. */
export function formatDryRunValue(value: unknown): string {
  if (value === null || value === undefined) return DRY_RUN_EMPTY_CELL;
  if (typeof value === 'string') return value.trim() === '' ? DRY_RUN_EMPTY_CELL : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
